/**
 * BikyBag Wishlist
 * Stores items as { id, handle } so the wishlist page can fetch via /products/{handle}.js
 * - localStorage for guests
 * - Customer metafield sync for logged-in customers
 */

const WISHLIST_KEY = 'bikybag_wishlist';
const META_NAMESPACE = 'custom';
const META_KEY = 'wishlist';
const TRACK_ENDPOINT = 'https://bikybag-notify.vercel.app/api/wishlist-track';

const BikyBagWishlist = (() => {

  /* ── localStorage ─────────────────────────────── */

  function getLocal() {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return normalize(raw);
    } catch { return []; }
  }

  function setLocal(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  }

  function normalize(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.map(entry => {
      if (typeof entry === 'string') return { id: entry, handle: null };
      if (entry && entry.id) return { id: String(entry.id), handle: entry.handle || null };
      return null;
    }).filter(Boolean);
  }

  /* ── Storefront API (logged-in sync) ──────────── */

  async function storefrontFetch(query, variables = {}) {
    const token = window.STOREFRONT_TOKEN || '';
    if (!token) return null;
    try {
      const res = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables })
      });
      return res.json();
    } catch { return null; }
  }

  function getCustomerToken() {
    return window.__st?.ctoken || getCookie('_shopify_sa_t') || '';
  }

  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : '';
  }

  function isLoggedIn() {
    return !!window.isCustomerLoggedIn;
  }

  async function getRemote() {
    const token = getCustomerToken();
    if (!token) return [];
    try {
      const data = await storefrontFetch(`
        query {
          customer(customerAccessToken: "${token}") {
            metafield(namespace: "${META_NAMESPACE}", key: "${META_KEY}") { value }
          }
        }
      `);
      const val = data?.data?.customer?.metafield?.value;
      return val ? normalize(JSON.parse(val)) : [];
    } catch { return []; }
  }

  async function saveRemote(items) {
    const token = getCustomerToken();
    if (!token) return;
    try {
      await storefrontFetch(`
        mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
          customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
            customer { id }
          }
        }
      `, {
        customerAccessToken: token,
        customer: {
          metafields: [{
            namespace: META_NAMESPACE, key: META_KEY,
            value: JSON.stringify(items), type: 'json'
          }]
        }
      });
    } catch (e) { console.warn('Wishlist remote save failed', e); }
  }

  /* ── core API ─────────────────────────────────── */

  async function getAll() {
    if (isLoggedIn()) {
      const remote = await getRemote();
      const local = getLocal();
      const map = new Map();
      [...remote, ...local].forEach(it => map.set(it.id, it));
      const merged = [...map.values()];
      if (local.length) setLocal([]);
      return merged;
    }
    return getLocal();
  }

  async function save(items) {
    if (isLoggedIn()) await saveRemote(items);
    else setLocal(items);
    broadcastChange(items);
  }

  async function toggle(id, handle, title) {
    const items = await getAll();
    const sid = String(id);
    const exists = items.some(i => i.id === sid);
    const next = exists
      ? items.filter(i => i.id !== sid)
      : [...items, { id: sid, handle: handle || null }];
    await save(next);
    trackEvent(sid, handle, title, exists ? 'remove' : 'add');
    return !exists;
  }

  // Fire-and-forget: never block or break the UI if tracking fails.
  function trackEvent(id, handle, title, action) {
    try {
      const body = JSON.stringify({
        product_id: id,
        product_handle: handle || null,
        product_title: title || null,
        action
      });
      // keepalive lets the request survive a page navigation
      fetch(TRACK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      }).catch(() => {});
    } catch { /* ignore */ }
  }

  async function has(id) {
    const items = await getAll();
    return items.some(i => i.id === String(id));
  }

  function broadcastChange(items) {
    updateCountBadge(items.length);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
  }

  function updateCountBadge(count) {
    document.querySelectorAll('.wishlist-count').forEach(badge => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  }

  /* ── button rendering ─────────────────────────── */

  function heartOutline() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
  }
  function heartFilled() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
  }

  function setButtonState(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    const icon = btn.querySelector('.wishlist-icon');
    if (icon) icon.innerHTML = active ? heartFilled() : heartOutline();
  }

  async function initButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-btn]');
    for (const btn of buttons) {
      const id = btn.dataset.productId;
      if (!id) continue;
      setButtonState(btn, await has(id));
      if (btn.dataset.wishlistBound) continue;
      btn.dataset.wishlistBound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
        const handle = btn.dataset.productHandle || null;
        const title = btn.dataset.productTitle || null;
        const nowActive = await toggle(id, handle, title);
        setButtonState(btn, nowActive);
        btn.disabled = false;
        showToast(nowActive ? 'Added to wishlist' : 'Removed from wishlist');
      });
    }
  }

  /* ── toast ────────────────────────────────────── */

  let toastTimer;
  function showToast(msg) {
    let toast = document.getElementById('wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2200);
  }

  /* ── add to cart ──────────────────────────────── */

  async function addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  /* ── wishlist page ────────────────────────────── */

  async function fetchProductByHandle(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return res.json();
    } catch { return null; }
  }

  function firstAvailableVariant(p) {
    if (!p.variants || !p.variants.length) return null;
    return p.variants.find(v => v.available) || p.variants[0];
  }

  async function renderWishlistPage() {
    const container = document.getElementById('wishlist-page-items');
    if (!container) return;

    container.innerHTML = '<p class="wishlist-loading">Loading your wishlist…</p>';
    const items = await getAll();

    if (!items.length) {
      container.innerHTML = '<p class="wishlist-empty">Your wishlist is empty.</p>';
      return;
    }

    const withHandles = items.filter(i => i.handle);
    const products = await Promise.all(withHandles.map(i => fetchProductByHandle(i.handle)));
    const valid = products.filter(Boolean);

    if (!valid.length) {
      container.innerHTML = '<p class="wishlist-empty">Your saved items need to be re-added to show here.</p>';
      return;
    }

    container.innerHTML = valid.map(p => {
      const img = p.featured_image || (p.images && p.images[0]) || '';
      const price = typeof p.price === 'number'
        ? (p.price / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
        : '';
      const variant = firstAvailableVariant(p);
      const available = variant && variant.available;
      const variantId = variant ? variant.id : '';
      const cartBtn = available
        ? `<button class="wishlist-item__add button button--primary" data-add-variant="${variantId}">Add to cart</button>`
        : `<button class="wishlist-item__add button button--secondary" disabled>Sold out</button>`;
      return `
        <div class="wishlist-item" data-product-id="${p.id}">
          <a href="/products/${p.handle}" class="wishlist-item__image">
            ${img ? `<img src="${img}" alt="${p.title}" loading="lazy">` : ''}
          </a>
          <div class="wishlist-item__info">
            <a href="/products/${p.handle}" class="wishlist-item__title">${p.title}</a>
            <p class="wishlist-item__price">${price}</p>
          </div>
          ${cartBtn}
          <button class="wishlist-item__remove" data-remove-id="${p.id}" aria-label="Remove from wishlist">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="18" height="18"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>`;
    }).join('');

    // remove handlers
    container.querySelectorAll('[data-remove-id]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = String(btn.dataset.removeId);
        const current = await getAll();
        await save(current.filter(i => i.id !== id));
        btn.closest('.wishlist-item').remove();
        if (!container.querySelectorAll('.wishlist-item').length) {
          container.innerHTML = '<p class="wishlist-empty">Your wishlist is empty.</p>';
        }
      });
    });

    // add-to-cart handlers
    container.querySelectorAll('[data-add-variant]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.addVariant;
        if (!variantId) return;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          await addToCart(variantId);
          btn.textContent = 'Added ✓';
          showToast('Added to cart');
          // refresh cart drawer/count if the theme exposes it
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
        } catch {
          btn.textContent = original;
          btn.disabled = false;
          showToast('Could not add to cart');
        }
      });
    });
  }

  /* ── init ─────────────────────────────────────── */

  function init() {
    initButtons();
    renderWishlistPage();
    getAll().then(items => updateCountBadge(items.length));
    document.addEventListener('wishlist:reinit', initButtons);
    observeNewButtons();
  }

  function observeNewButtons() {
    let scheduled = false;
    const observer = new MutationObserver((mutations) => {
      const hasNewButton = mutations.some(m =>
        [...m.addedNodes].some(node =>
          node.nodeType === 1 &&
          (node.matches?.('[data-wishlist-btn]') || node.querySelector?.('[data-wishlist-btn]'))
        )
      );
      if (hasNewButton && !scheduled) {
        scheduled = true;
        setTimeout(() => { initButtons(); scheduled = false; }, 100);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { toggle, has, getAll, save };
})();

window.BikyBagWishlist = BikyBagWishlist;